import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, Clock, UserCog, Ticket, ShieldAlert, FileClock,
  PackageCheck, ClipboardCheck, Loader2, CheckCircle2, ArrowRight,
} from 'lucide-react';
import {
  onboardingWorkflowAPI, communicationGovernanceAPI, complianceAPI,
  crmFollowupsAPI, suppliesAPI, operationsPulseAPI,
} from '@/lib/api';

/**
 * ActionCenter — the single, authoritative "what needs to be done" section.
 * Replaces the old competing bands (AttentionRequired / AgentRequiredActions)
 * and absorbs follow-ups, compliance, tickets, equipment and onboarding so the
 * dashboard never contradicts itself.
 *
 * Reliability rules:
 *  - Items are grouped: OVERDUE · ACTION REQUIRED · DUE SOON.
 *  - "Nothing needs your attention" shows ONLY when every source loaded
 *    successfully AND all counts are zero (true empty state).
 *  - If a source failed to load we surface a data-error note instead of a
 *    false-positive empty state.
 *  - Each item declares who must act and links to the real module.
 *
 * A source only appears for a user when THEY are expected to act (e.g. an
 * agent sees only their own compliance/follow-ups; KR sees the KR queues).
 */

const CAT = { OVERDUE: 'overdue', ACTION: 'action', SOON: 'soon' };
const CAT_META = {
  [CAT.OVERDUE]: { label: 'Overdue', tone: 'text-rose-600' },
  [CAT.ACTION]: { label: 'Action Required', tone: 'text-amber-600' },
  [CAT.SOON]: { label: 'Due Soon', tone: 'text-sky-600' },
};

const num = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);

export const ActionCenter = ({ scope = 'agent', extras = {}, parentLoading = false }) => {
  const [state, setState] = useState({ loading: true, items: [], errored: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const items = [];
      let errored = false;

      // Shared: CRM follow-ups (server scopes to the current user).
      const followSummary = await crmFollowupsAPI.summary()
        .then((r) => r.data).catch(() => { errored = true; return null; });
      if (followSummary) {
        const overdue = num(followSummary.overdue ?? followSummary.overdue_count);
        const soon = num(followSummary.soon ?? followSummary.due_soon ?? followSummary.today);
        if (overdue > 0) items.push({
          key: 'followups-overdue', category: CAT.OVERDUE, count: overdue,
          title: 'Overdue follow-ups', who: scope === 'admin' ? 'KR / assigned agent' : 'You',
          description: 'Follow-ups past their due date.', icon: FileClock,
          href: '/dashboard?followup_window=overdue',
        });
        if (soon > 0) items.push({
          key: 'followups-soon', category: CAT.SOON, count: soon,
          title: 'Follow-ups due soon', who: scope === 'admin' ? 'KR / assigned agent' : 'You',
          description: 'Follow-ups coming up shortly.', icon: Clock,
          href: '/dashboard?followup_window=soon',
        });
      }

      // Shared: overdue equipment returns.
      const eq = await suppliesAPI.overdueLoansCount()
        .then((r) => r.data).catch(() => null);
      const eqCount = num(eq?.count ?? eq?.overdue ?? eq);
      if (eqCount > 0) items.push({
        key: 'equipment-overdue', category: CAT.OVERDUE, count: eqCount,
        title: 'Equipment return overdue', who: scope === 'admin' ? 'KR / agent' : 'You',
        description: 'Picked-up equipment past its return date.', icon: PackageCheck,
        href: '/supplies',
      });

      if (scope === 'admin') {
        // Missing info + tickets come from /dashboard/stats (passed in).
        const missing = num(extras.missingInfoCount);
        if (missing > 0) items.push({
          key: 'missing-info', category: CAT.ACTION, count: missing,
          title: 'Missing information', who: 'Field agent (KR follow-up)',
          description: 'Contracting requests waiting on details from the field.',
          icon: AlertTriangle, href: '/requests?status=Missing_Info',
        });
        const tickets = num(extras.openTicketsCount);
        if (tickets > 0) items.push({
          key: 'tickets', category: CAT.ACTION, count: tickets,
          title: 'Support tickets', who: 'KR',
          description: 'Open tickets awaiting a KR response.', icon: Ticket, href: '/tickets',
        });

        // Stalled onboarding (>3 days without progress).
        const runs = await onboardingWorkflowAPI.listRuns({ status: 'in_progress', limit: 500 })
          .then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => { errored = true; return null; });
        if (runs) {
          const stalled = runs.filter((run) => run?.status === 'in_progress' && run?.updated_at
            && Date.now() - new Date(run.updated_at).getTime() > 3 * 864e5).length;
          if (stalled > 0) items.push({
            key: 'onboarding', category: CAT.ACTION, count: stalled,
            title: 'Stalled onboarding', who: 'KR',
            description: 'Onboarding runs with no progress in 3+ days.', icon: UserCog,
            href: '/onboarding-workflows',
          });
        }

        // Communication governance pending requests only (actionable).
        const gov = await communicationGovernanceAPI.getSummary()
          .then((r) => r.data).catch(() => null); // 403 for non-KR is fine, not an error surface
        const govPending = num(gov?.pending_agency_requests) + num(gov?.pending_agent_requests);
        if (govPending > 0) items.push({
          key: 'governance', category: CAT.ACTION, count: govPending,
          title: 'Governance requests', who: 'KR',
          description: 'Communication policy requests awaiting KR review.', icon: ShieldAlert,
          href: '/governance/communication-policy',
        });

        // Compliance — agents missing required documents (KR intervention).
        const comp = await complianceAPI.summary()
          .then((r) => r.data).catch(() => { errored = true; return null; });
        const compCount = comp ? num((comp.agents_with_missing || []).length) : 0;
        if (compCount > 0) items.push({
          key: 'compliance', category: CAT.ACTION, count: compCount,
          title: 'Compliance documents', who: 'KR (agent to provide)',
          description: 'Agents with missing required documents.', icon: ClipboardCheck,
          href: '/compliance',
        });

        // Contracting SLA breaches (Phase 5) — carrier + release overdue.
        const contracting = await operationsPulseAPI.contractingActions()
          .then((r) => r.data).catch(() => null); // 403 for non-KR is fine
        if (contracting) {
          const thr = num(contracting.threshold_days) || 7;
          const carrierOverdue = num(contracting.carrier_overdue);
          if (carrierOverdue > 0) items.push({
            key: 'carrier-overdue', category: CAT.OVERDUE, count: carrierOverdue,
            title: 'Carrier response overdue', who: 'KR (carrier follow-up)',
            description: `Requests awaiting a carrier reply for ${thr}+ days.`,
            icon: FileClock, href: '/requests?status=Pending_Carrier',
          });
          const releaseOverdue = num(contracting.release_overdue);
          if (releaseOverdue > 0) items.push({
            key: 'release-overdue', category: CAT.OVERDUE, count: releaseOverdue,
            title: 'Release overdue', who: 'KR',
            description: `Requests stuck awaiting a release for ${thr}+ days.`,
            icon: ShieldAlert, href: '/requests?status=Pending_Release',
          });
        }
      } else {
        // AGENT scope — only what the agent must personally act on.
        const compCount = num(extras.complianceCount);
        if (compCount > 0) items.push({
          key: 'compliance', category: CAT.ACTION, count: compCount,
          title: 'Complete your compliance', who: 'You',
          description: 'Required documents/items still needed from you.', icon: ClipboardCheck,
          href: '#compliance-tracker',
        });
        const onboarding = num(extras.onboardingCount);
        if (onboarding > 0) items.push({
          key: 'onboarding', category: CAT.ACTION, count: onboarding,
          title: 'Finish onboarding', who: 'You',
          description: 'Onboarding steps still waiting on you.', icon: UserCog,
          href: '#onboarding-checklist',
        });
      }

      if (!cancelled) setState({ loading: false, items, errored });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, extras.missingInfoCount, extras.openTicketsCount, extras.complianceCount, extras.onboardingCount]);

  if (parentLoading || state.loading) {
    return (
      <section className="mb-8" data-testid="action-center-loading">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Action Center</h2>
        <div className="rounded-xl border border-slate-200 bg-white px-6 py-8 flex items-center justify-center">
          <Loader2 className="animate-spin text-slate-400" size={18} />
        </div>
      </section>
    );
  }

  const total = state.items.reduce((n, i) => n + i.count, 0);
  const groups = [CAT.OVERDUE, CAT.ACTION, CAT.SOON]
    .map((cat) => ({ cat, items: state.items.filter((i) => i.category === cat) }))
    .filter((g) => g.items.length > 0);

  return (
    <section className="mb-8" data-testid="action-center">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {scope === 'admin' ? "What KR needs to act on" : 'What I need to do'}
        </h2>
        {total > 0 && (
          <span className="text-xs font-medium text-slate-500" data-testid="action-center-total">
            {total} item{total === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {state.errored && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-700" data-testid="action-center-error">
          We couldn't load some items. Counts below may be incomplete — please refresh.
        </div>
      )}

      {groups.length === 0 ? (
        state.errored ? (
          <div className="rounded-xl border border-slate-200 bg-white px-6 py-8 text-center" data-testid="action-center-error-empty">
            <p className="text-sm text-slate-600">We couldn't load your action items right now.</p>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-emerald-200 bg-emerald-50/40 px-6 py-8 text-center" data-testid="action-center-empty">
            <CheckCircle2 className="mx-auto text-emerald-500 mb-2" size={22} />
            <p className="text-base font-heading font-semibold text-slate-800">
              {scope === 'admin' ? 'Nothing needs KR’s attention' : 'You’re all caught up'}
            </p>
            <p className="text-sm text-slate-500 mt-1">No action is required from you right now.</p>
          </div>
        )
      ) : (
        <div className="space-y-4">
          {groups.map(({ cat, items }) => (
            <div key={cat}>
              <p className={`text-[11px] font-semibold uppercase tracking-wider mb-2 ${CAT_META[cat].tone}`}>
                {CAT_META[cat].label}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <Link
                      key={it.key}
                      to={it.href}
                      data-testid={`action-item-${it.key}`}
                      className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:shadow-md hover:border-slate-300 transition-all"
                    >
                      <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 group-hover:bg-slate-900 transition-colors">
                        <Icon size={16} className="text-slate-600 group-hover:text-white transition-colors" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-slate-900 tabular-nums">{it.count}</span>
                          <span className="text-sm font-medium text-slate-800 truncate">{it.title}</span>
                        </div>
                        <p className="text-[11px] text-slate-500 truncate">{it.description}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Owner: {it.who}</p>
                      </div>
                      <ArrowRight size={15} className="text-slate-300 group-hover:text-slate-600 shrink-0" />
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ActionCenter;
